import { useRef, useState } from "react";
import type { GeneratedStatus } from "@keydials/api-client";
import { HELP_ANCHORS } from "@keydials/ui";
import { Icon } from "../components/Icon";
import { StatusDot } from "../components/primitives";
import { hexA } from "../lib/ui";
import { AnchoredMenu } from "../components/AnchoredMenu";

const UNSET = "#94a3b8";

/**
 * A status pill that opens a dropdown of palette statuses. Used for a simple
 * dial's segment and for each (item × dimension) of an itemised dial. Statuses
 * the caller may not set are shown but disabled (the server is the real gate).
 */
export function StatusPicker({
  statuses,
  current,
  canSet,
  onPick,
  onClear,
  emptyLabel = "Set status",
  helpAnchor = HELP_ANCHORS.apStatus,
}: {
  statuses: GeneratedStatus[];
  current?: GeneratedStatus;
  canSet: (status: GeneratedStatus) => boolean;
  onPick: (statusKey: string) => void;
  /** Offer a "No status" row that clears the current status (KEY-479). Only a section has an unset state
   * worth choosing — an action point always carries a status — so most callers leave this off. */
  onClear?: () => void;
  /** The pill's wording while nothing is set. */
  emptyLabel?: string;
  helpAnchor?: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const colour = current?.colour ?? UNSET;
  const ordered = [...statuses].sort((a, b) => a.order - b.order);
  // Clearing is gated like setting: against the status coming OFF, which is what the server checks.
  const canClear = !!onClear && !!current && canSet(current);

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        data-help-anchor={helpAnchor}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "7px 11px",
          borderRadius: "var(--radius-sm)",
          cursor: "pointer",
          border: `1px solid ${hexA(colour, 0.3)}`,
          background: hexA(colour, 0.12),
          color: colour,
          font: "inherit",
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        <StatusDot colour={colour} size={9} />
        {current?.name ?? emptyLabel}
        <Icon name="chevDown" size={14} />
      </button>
      {/* Portalled (KEY-532): the Dynamic workspace scrolls the card stack, and any overflow but `visible`
          clips every descendant — a status list cut off at the bottom of a card hides exactly the statuses
          you cannot then pick. */}
      <AnchoredMenu anchorRef={triggerRef} open={open} onDismiss={() => setOpen(false)} minWidth={200} zIndex={30}>
        <>
            {ordered.map((opt) => {
              const isCurrent = opt.key === current?.key;
              const allowed = canSet(opt);
              return (
                <button
                  key={opt.key}
                  onClick={() => {
                    setOpen(false);
                    if (!isCurrent && allowed) {
                      onPick(opt.key);
                    }
                  }}
                  disabled={!allowed && !isCurrent}
                  title={allowed ? opt.description ?? opt.name : "You don't have permission to set this status"}
                  className="kd-row-hover"
                  style={{
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    gap: 9,
                    padding: "9px 10px",
                    border: "none",
                    background: isCurrent ? "var(--accent-soft)" : "none",
                    borderRadius: "var(--radius-sm)",
                    cursor: allowed && !isCurrent ? "pointer" : "default",
                    font: "inherit",
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "var(--text)",
                    opacity: allowed || isCurrent ? 1 : 0.45,
                  }}
                >
                  <StatusDot colour={opt.colour} size={9} />
                  {opt.name}
                  {isCurrent && <Icon name="check" size={14} style={{ marginLeft: "auto", color: "var(--accent-text)" }} />}
                </button>
              );
            })}
            {onClear && (
              <button
                onClick={() => {
                  setOpen(false);
                  if (canClear) {
                    onClear();
                  }
                }}
                disabled={!canClear}
                title={current ? (canClear ? "Remove the status" : "You don't have permission to clear this status") : "No status is set"}
                className="kd-row-hover"
                style={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  gap: 9,
                  padding: "9px 10px",
                  border: "none",
                  borderTop: "var(--border-w) solid var(--border)",
                  background: current ? "none" : "var(--accent-soft)",
                  borderRadius: "var(--radius-sm)",
                  cursor: canClear ? "pointer" : "default",
                  font: "inherit",
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: "var(--text-muted)",
                  opacity: canClear || !current ? 1 : 0.45,
                }}
              >
                <StatusDot colour={UNSET} size={9} />
                No status
                {!current && <Icon name="check" size={14} style={{ marginLeft: "auto", color: "var(--accent-text)" }} />}
              </button>
            )}
        </>
      </AnchoredMenu>
    </div>
  );
}
